import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, Save, BookOpen, Microscope, GraduationCap, Palette, Pencil, School, Book, Brain, Atom, Sparkles, Target, Heart, Star, Award, Users, Building2, Laptop, Music, Camera, Globe, Code, Calculator, Paintbrush, ChevronDown, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';

const EducationLevelsManagement = () => {
  const [levels, setLevels] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [editingLevel, setEditingLevel] = useState(null);
  const [isIconPopoverOpen, setIsIconPopoverOpen] = useState(false);
  const [isColorPopoverOpen, setIsColorPopoverOpen] = useState(false);
  const [formData, setFormData] = useState({
    level_key: '',
    title: '',
    age_range: '',
    description: '',
    philosophy: '',
    image_url: '',
    icon_name: 'BookOpen',
    color: 'from-blue-500 to-blue-600',
    order_index: 0,
    is_active: true,
    curriculum: [],
    features: [],
  });
  const [curriculumItem, setCurriculumItem] = useState('');
  const [featureItem, setFeatureItem] = useState('');
  const [editingCurriculumIndex, setEditingCurriculumIndex] = useState(null);
  const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
  const [editingCurriculumValue, setEditingCurriculumValue] = useState('');
  const [editingFeatureValue, setEditingFeatureValue] = useState('');

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const response = await fetch('http://localhost/api/education.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Eğitim kademeleri yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // is_active değerlerini normalize et
      const normalizedData = data.map(item => ({
        ...item,
        is_active: item.is_active === 1 || item.is_active === '1' || item.is_active === true ? 1 : 0,
      }));
      
      setLevels(normalizedData);
    } catch (error) {
      console.error('Error fetching levels:', error);
      toast.error('Eğitim kademeleri yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'order_index' ? parseInt(value) || 0 : value),
    }));
  };

  const deleteOldImage = async (imageUrl) => {
    if (!imageUrl || !imageUrl.includes('/uploads/')) return;
    
    try {
      await fetch('http://localhost/api/delete-image.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_url: imageUrl }),
      });
    } catch (error) {
      // Sessizce hata yut (resim silinmese bile devam et)
      console.error('Error deleting old image:', error);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir resim dosyası seçin');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('Dosya boyutu 25MB\'dan büyük olamaz');
      return;
    }

    // Eski resmi sakla (yeni resim yüklendikten sonra silmek için)
    const oldImageUrl = formData.image_url;

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      
      const response = await fetch('http://localhost/api/upload.php', {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData,
      });
      
      let text = '';
      try {
        text = await response.text();
      } catch (e) {
        console.error('Error reading response:', e);
        toast.error('Görsel yüklenirken hata oluştu');
        return;
      }
      
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = text ? JSON.parse(text) : {};
        } catch (e) {
          errorData = { error: text || 'Görsel yüklenirken hata oluştu' };
        }
        toast.error(errorData.error || 'Görsel yüklenirken hata oluştu');
        return;
      }
      
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        console.error('Response text:', text);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }
      
      // URL'yi kontrol et ve düzelt
      let imageUrl = data.url || data.image_url || '';
      
      // Eğer sadece filename varsa, tam URL oluştur
      if (!imageUrl && data.filename) {
        imageUrl = `/uploads/${data.filename}`;
      }
      
      // Relative path'i tam URL'ye çevir
      if (imageUrl && !imageUrl.startsWith('http')) {
        // Eğer /uploads/ ile başlıyorsa, http://localhost ekle
        if (imageUrl.startsWith('/uploads/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else if (imageUrl.startsWith('/')) {
          imageUrl = `http://localhost${imageUrl}`;
        } else {
          imageUrl = `http://localhost/uploads/${imageUrl}`;
        }
      }
      
      if (imageUrl) {
        // Yeni resim başarıyla yüklendi, eski resmi sil
        if (oldImageUrl && oldImageUrl !== imageUrl && oldImageUrl.includes('/uploads/')) {
          await deleteOldImage(oldImageUrl);
        }
        
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        toast.success('Görsel başarıyla yüklendi');
      } else {
        toast.error('Görsel URL alınamadı');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
    }
  };

  const addCurriculumItem = () => {
    if (curriculumItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        curriculum: [...prev.curriculum, curriculumItem.trim()],
      }));
      setCurriculumItem('');
    }
  };

  const removeCurriculumItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      curriculum: prev.curriculum.filter((_, i) => i !== index),
    }));
  };

  const addFeatureItem = () => {
    if (featureItem.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featureItem.trim()],
      }));
      setFeatureItem('');
    }
  };

  const removeFeatureItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const startEditingCurriculum = (index) => {
    setEditingCurriculumIndex(index);
    setEditingCurriculumValue(formData.curriculum[index]);
  };

  const saveCurriculumEdit = () => {
    if (editingCurriculumValue.trim() && editingCurriculumIndex !== null) {
      setFormData((prev) => {
        const newCurriculum = [...prev.curriculum];
        newCurriculum[editingCurriculumIndex] = editingCurriculumValue.trim();
        return { ...prev, curriculum: newCurriculum };
      });
      setEditingCurriculumIndex(null);
      setEditingCurriculumValue('');
    }
  };

  const cancelCurriculumEdit = () => {
    setEditingCurriculumIndex(null);
    setEditingCurriculumValue('');
  };

  const startEditingFeature = (index) => {
    setEditingFeatureIndex(index);
    setEditingFeatureValue(formData.features[index]);
  };

  const saveFeatureEdit = () => {
    if (editingFeatureValue.trim() && editingFeatureIndex !== null) {
      setFormData((prev) => {
        const newFeatures = [...prev.features];
        newFeatures[editingFeatureIndex] = editingFeatureValue.trim();
        return { ...prev, features: newFeatures };
      });
      setEditingFeatureIndex(null);
      setEditingFeatureValue('');
    }
  };

  const cancelFeatureEdit = () => {
    setEditingFeatureIndex(null);
    setEditingFeatureValue('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingLevel
        ? `http://localhost/api/education.php?id=${editingLevel.id}`
        : 'http://localhost/api/education.php';
      
      const method = editingLevel ? 'PUT' : 'POST';
      
      // Level_key'i otomatik oluştur (başlıktan slug oluştur)
      const level_key = editingLevel?.level_key || formData.title.toLowerCase()
        .replace(/ş/g, 's')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/ı/g, 'i')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      const requestBody = {
        title: formData.title,
        age_range: formData.age_range,
        description: formData.description,
        philosophy: formData.philosophy,
        image_url: formData.image_url,
        icon_name: formData.icon_name || 'BookOpen',
        color: formData.color,
        order_index: formData.order_index,
        is_active: formData.is_active === true || formData.is_active === 1 || formData.is_active === '1' ? 1 : 0,
        level_key: level_key,
        curriculum: Array.isArray(formData.curriculum) ? formData.curriculum : [],
        features: Array.isArray(formData.features) ? formData.features : [],
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      // Response body'yi sadece bir kez oku
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
        console.error('Response text:', text);
        toast.error('Sunucu yanıtı işlenemedi');
        return;
      }

      if (!response.ok) {
        toast.error(data.error || 'Eğitim kademesi kaydedilirken hata oluştu');
        return;
      }
      
      // Eğer API'den güncellenmiş level verisi döndüyse, levels state'ini direkt güncelle
      if (editingLevel && data.id) {
        setLevels(prevLevels => {
          return prevLevels.map(level => {
            if (level.id === data.id || level.id === editingLevel.id) {
              return { ...level, ...data };
            }
            return level;
          });
        });
      }
      
      toast.success(editingLevel ? 'Eğitim kademesi güncellendi' : 'Eğitim kademesi eklendi');
      setIsModalOpen(false);
      setEditingLevel(null);
      resetForm();
      
      // Güncellenmiş veriyi almak için fetchLevels çağır
      fetchLevels();
    } catch (error) {
      console.error('Error saving level:', error);
      toast.error('Eğitim kademesi kaydedilirken hata oluştu');
    }
  };

  const resetForm = () => {
    setIsIconPopoverOpen(false);
    setIsColorPopoverOpen(false);
    setFormData({
      title: '',
      age_range: '',
      description: '',
      philosophy: '',
      image_url: '',
      icon_name: 'BookOpen',
      color: 'from-blue-500 to-blue-600',
      order_index: 0,
      is_active: true,
      curriculum: [],
      features: [],
    });
    setCurriculumItem('');
    setFeatureItem('');
    setEditingCurriculumIndex(null);
    setEditingFeatureIndex(null);
    setEditingCurriculumValue('');
    setEditingFeatureValue('');
  };

  const handleEdit = async (level) => {
    try {
      const response = await fetch(`http://localhost/api/education.php?key=${level.level_key}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        toast.error('Eğitim kademesi detayları yüklenemedi');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      setEditingLevel(data);
      // Icon name'i önce API'den, sonra level parametresinden, son olarak default değerden al
      // Eğer data.icon_name boş string ise, level.icon_name'i kullan
      const iconName = (data.icon_name && data.icon_name.trim() !== '') 
        ? data.icon_name 
        : (level.icon_name && level.icon_name.trim() !== '' 
          ? level.icon_name 
          : 'BookOpen');
      
      // is_active değerini normalize et (boolean'a çevir)
      const normalizeIsActive = (value) => {
        if (value === true || value === 1 || value === '1') return true;
        if (value === false || value === 0 || value === '0') return false;
        return true; // default
      };
      
      const isActiveValue = data.is_active !== undefined 
        ? normalizeIsActive(data.is_active)
        : (level.is_active !== undefined 
          ? normalizeIsActive(level.is_active) 
          : true);
      
      const newFormData = {
        level_key: data.level_key || level.level_key || '',
        title: data.title || level.title || '',
        age_range: data.age_range || level.age_range || '',
        description: data.description || level.description || '',
        philosophy: data.philosophy || level.philosophy || '',
        image_url: data.image_url || level.image_url || '',
        icon_name: iconName,
        color: data.color || level.color || 'from-blue-500 to-blue-600',
        order_index: data.order_index !== undefined ? data.order_index : (level.order_index !== undefined ? level.order_index : 0),
        is_active: isActiveValue,
        curriculum: data.curriculum || level.curriculum || [],
        features: data.features || level.features || [],
      };
      
      setFormData(newFormData);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching level details:', error);
      toast.error('Eğitim kademesi detayları yüklenemedi');
    }
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      const response = await fetch(`http://localhost/api/education.php?id=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Eğitim kademesi silinirken hata oluştu');
        return;
      }

      toast.success('Eğitim kademesi silindi');
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      fetchLevels();
    } catch (error) {
      console.error('Error deleting level:', error);
      toast.error('Eğitim kademesi silinirken hata oluştu');
    }
  };

  const openAddModal = () => {
    setEditingLevel(null);
    resetForm();
    setFormData((prev) => ({ ...prev, order_index: levels.length }));
    setIsModalOpen(true);
  };

  const handleToggleActive = async (level) => {
    // Mevcut durumu kontrol et - farklı formatları destekle
    const currentActive = level.is_active === 1 || level.is_active === '1' || level.is_active === true;
    const newActiveValue = currentActive ? 0 : 1;
    
    // State'i anında güncelle (optimistic update)
    setLevels(prev => prev.map(levelItem => 
      levelItem.id === level.id 
        ? { ...levelItem, is_active: newActiveValue }
        : levelItem
    ));
    
    try {
      // Önce tam veriyi çek (curriculum ve features dahil)
      const getResponse = await fetch(`http://localhost/api/education.php?id=${level.id}`, {
        credentials: 'include',
      });
      
      let fullLevelData = level;
      if (getResponse.ok) {
        const getText = await getResponse.text();
        const getData = getText ? JSON.parse(getText) : null;
        if (getData) {
          fullLevelData = getData;
        }
      }
      
      const response = await fetch(`http://localhost/api/education.php?id=${level.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: fullLevelData.title,
          age_range: fullLevelData.age_range,
          description: fullLevelData.description,
          philosophy: fullLevelData.philosophy,
          image_url: fullLevelData.image_url || '',
          icon_name: fullLevelData.icon_name || 'BookOpen',
          color: fullLevelData.color,
          order_index: fullLevelData.order_index,
          is_active: newActiveValue,
          curriculum: Array.isArray(fullLevelData.curriculum) ? fullLevelData.curriculum : [],
          features: Array.isArray(fullLevelData.features) ? fullLevelData.features : [],
        }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
      }

      if (!response.ok) {
        // Hata durumunda state'i geri al
        setLevels(prev => prev.map(levelItem => 
          levelItem.id === level.id 
            ? { ...levelItem, is_active: currentActive ? 1 : 0 }
            : levelItem
        ));
        toast.error(data.error || 'Durum güncellenirken hata oluştu');
        return;
      }

      // Başarılı - state'i güncelle ve verileri yeniden çek
      setLevels(prev => prev.map(levelItem => 
        levelItem.id === level.id 
          ? { ...levelItem, is_active: newActiveValue }
          : levelItem
      ));
      
      await fetchLevels();
      toast.success('Durum başarıyla güncellendi');
    } catch (error) {
      // Hata durumunda state'i geri al
      setLevels(prev => prev.map(levelItem => 
        levelItem.id === level.id 
          ? { ...levelItem, is_active: currentActive ? 1 : 0 }
          : levelItem
      ));
      console.error('Error toggling active:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const getIcon = (iconName) => {
    const iconMap = {
      BookOpen: <BookOpen className="w-6 h-6" />,
      Microscope: <Microscope className="w-6 h-6" />,
      GraduationCap: <GraduationCap className="w-6 h-6" />,
      School: <School className="w-6 h-6" />,
      Book: <Book className="w-6 h-6" />,
      Brain: <Brain className="w-6 h-6" />,
      Atom: <Atom className="w-6 h-6" />,
      Sparkles: <Sparkles className="w-6 h-6" />,
      Target: <Target className="w-6 h-6" />,
      Heart: <Heart className="w-6 h-6" />,
      Star: <Star className="w-6 h-6" />,
      Award: <Award className="w-6 h-6" />,
      Users: <Users className="w-6 h-6" />,
      Building2: <Building2 className="w-6 h-6" />,
      Laptop: <Laptop className="w-6 h-6" />,
      Music: <Music className="w-6 h-6" />,
      Camera: <Camera className="w-6 h-6" />,
      Globe: <Globe className="w-6 h-6" />,
      Code: <Code className="w-6 h-6" />,
      Calculator: <Calculator className="w-6 h-6" />,
      Paintbrush: <Paintbrush className="w-6 h-6" />,
    };
    return iconMap[iconName] || <BookOpen className="w-6 h-6" />;
  };

  const iconOptions = [
    { name: 'BookOpen', icon: <BookOpen className="w-6 h-6" />, label: 'Kitap' },
    { name: 'Microscope', icon: <Microscope className="w-6 h-6" />, label: 'Mikroskop' },
    { name: 'GraduationCap', icon: <GraduationCap className="w-6 h-6" />, label: 'Mezuniyet' },
    { name: 'School', icon: <School className="w-6 h-6" />, label: 'Okul' },
    { name: 'Book', icon: <Book className="w-6 h-6" />, label: 'Kitap 2' },
    { name: 'Brain', icon: <Brain className="w-6 h-6" />, label: 'Beyin' },
    { name: 'Atom', icon: <Atom className="w-6 h-6" />, label: 'Atom' },
    { name: 'Sparkles', icon: <Sparkles className="w-6 h-6" />, label: 'Parlama' },
    { name: 'Target', icon: <Target className="w-6 h-6" />, label: 'Hedef' },
    { name: 'Heart', icon: <Heart className="w-6 h-6" />, label: 'Kalp' },
    { name: 'Star', icon: <Star className="w-6 h-6" />, label: 'Yıldız' },
    { name: 'Award', icon: <Award className="w-6 h-6" />, label: 'Ödül' },
    { name: 'Users', icon: <Users className="w-6 h-6" />, label: 'Kullanıcılar' },
    { name: 'Building2', icon: <Building2 className="w-6 h-6" />, label: 'Bina' },
    { name: 'Laptop', icon: <Laptop className="w-6 h-6" />, label: 'Laptop' },
    { name: 'Music', icon: <Music className="w-6 h-6" />, label: 'Müzik' },
    { name: 'Camera', icon: <Camera className="w-6 h-6" />, label: 'Kamera' },
    { name: 'Globe', icon: <Globe className="w-6 h-6" />, label: 'Dünya' },
    { name: 'Code', icon: <Code className="w-6 h-6" />, label: 'Kod' },
    { name: 'Calculator', icon: <Calculator className="w-6 h-6" />, label: 'Hesap Makinesi' },
    { name: 'Paintbrush', icon: <Paintbrush className="w-6 h-6" />, label: 'Fırça' },
  ];

  const colorOptions = [
    { name: 'Mavi', value: 'from-blue-500 to-blue-600', preview: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    { name: 'Mor', value: 'from-purple-500 to-purple-600', preview: 'bg-gradient-to-r from-purple-500 to-purple-600' },
    { name: 'Pembe', value: 'from-pink-500 to-pink-600', preview: 'bg-gradient-to-r from-pink-500 to-pink-600' },
    { name: 'Kırmızı', value: 'from-red-500 to-red-600', preview: 'bg-gradient-to-r from-red-500 to-red-600' },
    { name: 'Turuncu', value: 'from-orange-500 to-orange-600', preview: 'bg-gradient-to-r from-orange-500 to-orange-600' },
    { name: 'Sarı', value: 'from-yellow-500 to-yellow-600', preview: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    { name: 'Yeşil', value: 'from-green-500 to-green-600', preview: 'bg-gradient-to-r from-green-500 to-green-600' },
    { name: 'Turkuaz', value: 'from-cyan-500 to-cyan-600', preview: 'bg-gradient-to-r from-cyan-500 to-cyan-600' },
    { name: 'Kırmızı Koyu', value: 'from-red-900 to-red-800', preview: 'bg-gradient-to-r from-red-900 to-red-800' },
    { name: 'Amber', value: 'from-amber-500 to-amber-600', preview: 'bg-gradient-to-r from-amber-500 to-amber-600' },
    { name: 'Indigo', value: 'from-indigo-500 to-indigo-600', preview: 'bg-gradient-to-r from-indigo-500 to-indigo-600' },
    { name: 'Teal', value: 'from-teal-500 to-teal-600', preview: 'bg-gradient-to-r from-teal-500 to-teal-600' },
  ];

  return (
    <AdminLayout
      addButton={
        <Button
          onClick={openAddModal}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white flex items-center gap-2"
        >
          <Plus size={20} />
          Yeni Kademe
        </Button>
      }
    >
      <div>
        <div className="mt-6">
          {levels.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz eğitim kademesi yok</h3>
              <p className="text-gray-600 mb-6">Yeni bir eğitim kademesi ekleyerek başlayın</p>
              <Button onClick={openAddModal} className="bg-gradient-to-r from-blue-500 to-blue-600">
                İlk Kademeyi Ekle
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {levels
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((level) => (
                  <div
                    key={level.id}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${level.color} text-white flex items-center justify-center`}>
                        {getIcon(level.icon_name || 'BookOpen')}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900">{level.title}</h3>
                        <p className="text-sm text-gray-500">{level.age_range}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{level.description}</p>
                    <div className="mb-3">
                      <button
                        onClick={() => handleToggleActive(level)}
                        className={`w-full px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          level.is_active === 1 || level.is_active === true || level.is_active === '1'
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {level.is_active === 1 || level.is_active === true || level.is_active === '1' ? 'Aktif' : 'Pasif'}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEdit(level)}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Edit size={16} className="mr-1" />
                        Düzenle
                      </Button>
                      <Button
                        onClick={() => handleDelete(level.id)}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLevel ? 'Eğitim Kademesini Düzenle' : 'Yeni Eğitim Kademesi Ekle'}</DialogTitle>
            <DialogDescription>
              {editingLevel ? 'Eğitim kademesi bilgilerini düzenleyin.' : 'Yeni bir eğitim kademesi ekleyin.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık *</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="İlkokul"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Yaş Aralığı</label>
              <Input
                name="age_range"
                value={formData.age_range}
                onChange={handleInputChange}
                placeholder="6-10 Yaş"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">İkon Seçin</label>
                <Popover key={formData.icon_name} open={isIconPopoverOpen} onOpenChange={setIsIconPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between"
                    >
                      <div className="flex items-center gap-2">
                        {getIcon(formData.icon_name || 'BookOpen')}
                        <span>{iconOptions.find(i => i.name === formData.icon_name)?.label || 'İkon Seçin'}</span>
                      </div>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" side="bottom" align="start" modal={false}>
                    <div className="grid grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                      {iconOptions.map((iconOption) => (
                        <button
                          key={iconOption.name}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, icon_name: iconOption.name }));
                            setIsIconPopoverOpen(false);
                          }}
                          className={`flex flex-col items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                            formData.icon_name === iconOption.name
                              ? 'border-red-900 bg-red-900/10 scale-105'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`${formData.icon_name === iconOption.name ? 'text-red-900' : 'text-gray-600'}`}>
                            {iconOption.icon}
                          </div>
                          <span className={`text-xs font-medium text-center ${
                            formData.icon_name === iconOption.name ? 'text-red-900' : 'text-gray-600'
                          }`}>
                            {iconOption.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Renk Seçin</label>
                <Popover open={isColorPopoverOpen} onOpenChange={setIsColorPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded ${colorOptions.find(c => c.value === formData.color)?.preview || 'bg-gradient-to-r from-blue-500 to-blue-600'}`}></div>
                        <span>{colorOptions.find(c => c.value === formData.color)?.name || 'Renk Seçin'}</span>
                      </div>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80" side="bottom" align="start" modal={false}>
                    <div className="grid grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                      {colorOptions.map((colorOption) => (
                        <button
                          key={colorOption.value}
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, color: colorOption.value }));
                            setIsColorPopoverOpen(false);
                          }}
                          className={`flex flex-col items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                            formData.color === colorOption.value
                              ? 'border-red-900 bg-red-900/10 scale-105'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded ${colorOption.preview} ${formData.color === colorOption.value ? 'ring-2 ring-red-900' : ''}`}></div>
                          <span className={`text-xs font-medium ${
                            formData.color === colorOption.value ? 'text-red-900' : 'text-gray-600'
                          }`}>
                            {colorOption.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Açıklama</label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Eğitim kademesi açıklaması"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Eğitim Felsefesi</label>
              <Textarea
                name="philosophy"
                value={formData.philosophy}
                onChange={handleInputChange}
                placeholder="Eğitim felsefesi açıklaması"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Görsel Yükle</label>
              <div className="grid grid-cols-2 gap-4">
                {/* Sol: Yükleme Alanı */}
                <div
                  className="relative border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-900 hover:bg-red-50/50 transition-all cursor-pointer group"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.add('border-red-900', 'bg-red-50/50');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.remove('border-red-900', 'bg-red-50/50');
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.classList.remove('border-red-900', 'bg-red-50/50');
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith('image/')) {
                      await handleImageUpload(file);
                    } else {
                      toast.error('Lütfen geçerli bir resim dosyası seçin');
                    }
                  }}
                  onClick={() => {
                    const input = document.getElementById('image-upload-input');
                    input?.click();
                  }}
                >
                  <input
                    id="image-upload-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        await handleImageUpload(file);
                      }
                    }}
                  />
                  {!formData.image_url ? (
                    <>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-full bg-gray-100 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                          <Upload className="w-8 h-8 text-gray-400 group-hover:text-red-900 transition-colors" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">
                            Görsel sürükle ve bırak
                          </p>
                          <p className="text-xs text-gray-500">
                            veya tıklayarak seç
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          PNG, JPG, GIF (max. 25MB)
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Görseli değiştirmek için tıklayın</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sağ: Önizleme */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
                  {formData.image_url ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <img 
                        key={formData.image_url}
                        src={formData.image_url} 
                        alt="Önizleme" 
                        className="max-w-full max-h-48 object-contain rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.parentElement.querySelector('.image-error');
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                      />
                      <div className="image-error hidden text-center text-red-500 text-xs">
                        <p>Görsel yüklenemedi</p>
                        <p className="text-gray-400 break-all text-[10px] mt-1">{formData.image_url}</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData(prev => ({ ...prev, image_url: '' }));
                          const input = document.getElementById('image-upload-input');
                          if (input) input.value = '';
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Önizleme</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sıra</label>
                <Input
                  type="number"
                  name="order_index"
                  value={formData.order_index}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              <div className="flex items-center pt-8">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active === true || formData.is_active === 1 || formData.is_active === '1'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">Aktif</label>
              </div>
            </div>

            {/* Müfredat */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Müfredat</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={curriculumItem}
                  onChange={(e) => setCurriculumItem(e.target.value)}
                  placeholder="Müfredat öğesi ekle"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCurriculumItem())}
                />
                <Button type="button" onClick={addCurriculumItem} variant="outline">
                  Ekle
                </Button>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {formData.curriculum.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    {editingCurriculumIndex === index ? (
                      <>
                        <Input
                          value={editingCurriculumValue}
                          onChange={(e) => setEditingCurriculumValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveCurriculumEdit();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelCurriculumEdit();
                            }
                          }}
                          className="flex-1 text-sm"
                          autoFocus
                        />
                        <Button
                          type="button"
                          onClick={saveCurriculumEdit}
                          variant="ghost"
                          size="sm"
                        >
                          <Save size={14} />
                        </Button>
                        <Button
                          type="button"
                          onClick={cancelCurriculumEdit}
                          variant="ghost"
                          size="sm"
                        >
                          <X size={14} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{item}</span>
                        <Button
                          type="button"
                          onClick={() => startEditingCurriculum(index)}
                          variant="ghost"
                          size="sm"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          type="button"
                          onClick={() => removeCurriculumItem(index)}
                          variant="ghost"
                          size="sm"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Özellikler */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Öne Çıkan Özellikler</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={featureItem}
                  onChange={(e) => setFeatureItem(e.target.value)}
                  placeholder="Özellik ekle"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeatureItem())}
                />
                <Button type="button" onClick={addFeatureItem} variant="outline">
                  Ekle
                </Button>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {formData.features.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    {editingFeatureIndex === index ? (
                      <>
                        <Input
                          value={editingFeatureValue}
                          onChange={(e) => setEditingFeatureValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveFeatureEdit();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelFeatureEdit();
                            }
                          }}
                          className="flex-1 text-sm"
                          autoFocus
                        />
                        <Button
                          type="button"
                          onClick={saveFeatureEdit}
                          variant="ghost"
                          size="sm"
                        >
                          <Save size={14} />
                        </Button>
                        <Button
                          type="button"
                          onClick={cancelFeatureEdit}
                          variant="ghost"
                          size="sm"
                        >
                          <X size={14} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{item}</span>
                        <Button
                          type="button"
                          onClick={() => startEditingFeature(index)}
                          variant="ghost"
                          size="sm"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          type="button"
                          onClick={() => removeFeatureItem(index)}
                          variant="ghost"
                          size="sm"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
              >
                <Save size={16} className="mr-2" />
                {editingLevel ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu eğitim kademesini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default EducationLevelsManagement;

